import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAuth } from '@/api';
import { PageLayout, PageLoadingState } from '@/components/layout';
import { Button, Typography } from '@/primer/components';
import { usePostAuthenticateInvite } from '@/api/mutations/campaigns/usePostAuthenticateInvite';
import { campaignKeys } from '@/api/queryKeys';
import {
  clearSocialAuthContext,
  readSocialAuthContext,
  storeSocialAuthContext,
} from '@/utils/socialAuthContext';
import {
  getSocialCallbackStatus,
  resetSocialCallback,
  subscribeSocialCallback,
} from '@/utils/socialAuthCallback';
import { MS_PER_SECOND } from '@/utils/time';

const CALLBACK_TIMEOUT_MS = 10 * MS_PER_SECOND;

export function SocialAuthCallback({ onDirectFailure }: { onDirectFailure?: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAuthenticated, isLoading, refreshSession } = useAuth();
  const { mutateAsync } = usePostAuthenticateInvite();
  const status = useSyncExternalStore(subscribeSocialCallback, getSocialCallbackStatus);
  const [error, setError] = useState<
    'timeout' | 'completion-timeout' | 'authentication' | 'completion' | null
  >(null);
  const inFlight = useRef(false);
  const finished = useRef(false);
  const abandoned = useRef(false);

  useEffect(() => {
    abandoned.current = false;
    return () => {
      abandoned.current = true;
    };
  }, []);

  const complete = useCallback(async () => {
    if (inFlight.current || finished.current || abandoned.current) return;
    inFlight.current = true;
    setError(null);
    let failure: 'authentication' | 'completion' = 'authentication';
    const context = readSocialAuthContext();
    try {
      if (context?.kind !== 'invite') {
        const session = status === 'success' ? await fetchAuthSession() : undefined;
        if (abandoned.current) return;
        const signedIn = isAuthenticated || !!session?.tokens?.accessToken;
        const restored = signedIn && (await refreshSession());
        if (abandoned.current) return;
        clearSocialAuthContext();
        await navigate({ to: restored ? '/dashboard' : '/', replace: true });
        if (!restored && onDirectFailure) onDirectFailure();
        finished.current = true;
        return;
      }
      if (
        context.phase !== 'linked' &&
        ((context.phase === 'failed' && status !== 'success') ||
          status === 'failure' ||
          (context.phase === 'redirecting' && status === 'idle'))
      ) {
        // deliberate: OAuth failure restores invite choices; linking failures stay here for retry.
        try {
          storeSocialAuthContext({ ...context, phase: 'failed' });
        } catch {
          /* Recovery does not depend on persistence. */
        }
        await navigate({
          to: '/invite/auth',
          search: { token: context.magicToken },
          replace: true,
        });
        finished.current = true;
        return;
      }
      if (status !== 'success' && context.phase !== 'authenticated' && context.phase !== 'linked') {
        throw new Error('OAuth completion was not confirmed');
      }
      const session = await fetchAuthSession();
      if (abandoned.current) return;
      if (!session.tokens?.accessToken) throw new Error('OAuth session unavailable');
      const restored = await refreshSession();
      if (abandoned.current) return;
      if (!restored) throw new Error('OAuth session restoration failed');
      failure = 'completion';
      if (context.phase !== 'linked') {
        await mutateAsync({
          magicToken: context.magicToken,
          accessToken: session.tokens.accessToken.toString(),
        });
        if (abandoned.current) return;
        try {
          storeSocialAuthContext({ ...context, phase: 'linked' });
        } catch {
          // Linking succeeded; persistence is only needed if navigation is interrupted.
        }
        if (abandoned.current) return;
      }
      // Remove stale invite data; the proposal fetches the linked creator before enabling Accept.
      queryClient.removeQueries({ queryKey: campaignKeys.invite(context.magicToken), exact: true });
      await navigate({ to: '/invite', search: { token: context.magicToken }, replace: true });
      clearSocialAuthContext();
      finished.current = true;
    } catch {
      if (!abandoned.current) setError(failure);
    } finally {
      inFlight.current = false;
    }
  }, [
    isAuthenticated,
    mutateAsync,
    navigate,
    onDirectFailure,
    queryClient,
    refreshSession,
    status,
  ]);

  const returnToSignIn = async () => {
    try {
      abandoned.current = true;
      resetSocialCallback();
      const context = readSocialAuthContext();
      if (context?.kind === 'invite') {
        try {
          storeSocialAuthContext({ ...context, phase: 'failed' });
        } catch {
          /* Recovery does not depend on persistence. */
        }
        await navigate({
          to: '/invite/auth',
          search: { token: context.magicToken },
          replace: true,
        });
      } else {
        clearSocialAuthContext();
        await navigate({ to: '/', replace: true });
        if (onDirectFailure) onDirectFailure();
      }
    } catch {
      setError('authentication');
    }
  };

  useEffect(() => {
    const context = readSocialAuthContext();
    const restored =
      status === 'idle' &&
      (context?.kind === 'invite'
        ? context.phase === 'authenticated' || context.phase === 'linked'
        : isAuthenticated);
    if (!isLoading && (status === 'success' || restored) && error === 'timeout') {
      setError(null);
    }
  }, [error, isAuthenticated, isLoading, status]);

  useEffect(() => {
    const linked = readSocialAuthContext();
    if (
      !isLoading &&
      (status !== 'pending' || (linked?.kind === 'invite' && linked.phase === 'linked')) &&
      !error
    )
      void complete();
  }, [complete, error, isLoading, status]);

  useEffect(() => {
    if (error) return;
    const timeout = window.setTimeout(() => {
      if (!finished.current && !abandoned.current) {
        setError((current) => current ?? (inFlight.current ? 'completion-timeout' : 'timeout'));
      }
    }, CALLBACK_TIMEOUT_MS);
    return () => window.clearTimeout(timeout);
  }, [error]);

  if (!error) return <PageLoadingState size="large" />;
  return (
    <PageLayout className="py-8" gridProps={{ className: 'px-5' }}>
      <div className="flex flex-col gap-5">
        <Typography variant="header" size="h1" as="h1" align="center">
          {error === 'completion'
            ? 'We couldn’t finish your invitation.'
            : 'We couldn’t finish signing you in.'}
        </Typography>
        <div role="alert">
          <Typography variant="body" size="medium" align="center">
            {error === 'completion'
              ? 'Please try again to return to your invitation.'
              : 'Please return to sign-in and try again.'}
          </Typography>
        </div>
        {error === 'completion' && (
          <Button fullWidth onClick={() => void complete()}>
            Try again
          </Button>
        )}
        <Button
          variant={error === 'completion' ? 'secondary' : 'primary'}
          fullWidth
          onClick={() => void returnToSignIn()}
        >
          Return to sign-in
        </Button>
      </div>
    </PageLayout>
  );
}
